import { ERROR, PENDING, SUCCESS } from '../../design-system/constants';
import { AllActions } from '../../shared/redux/types';

import * as actions from './actions';
import { filterByFileName } from './operations';
import {
    ContractState,
    ContractAction,
    ContractStatusState,
    LegalInformationState,
    LegalInformationActions,
    LegalInformationStatusState,
    ContractEditStep,
    QuotePricingState,
    QuotePricingActions,
    QuotePricingStatusState,
    InstalmentActions,
    PaymentScheduleActions,
    PaymentScheduleStatusState,
} from './types';

const initialState = {
    entity: null,
    error: '',
    currentStep: ContractEditStep.TRAVEL_INFORMATION,
    shouldRecreatePaymentSchedule: false,
};

export const contractReducer = (
    state: ContractState = initialState,
    action: ContractAction | InstalmentActions | PaymentScheduleActions,
): ContractState => {
    switch (action.type) {
        case actions.fetchContractSuccessAction: {
            return {
                ...state,
                entity: action.response,
            };
        }
        case actions.fetchContractFailureAction: {
            return {
                ...state,
                error: action.error,
            };
        }
        case actions.postContractSuccessAction: {
            return {
                ...state,
                entity: action.response,
            };
        }
        case actions.setContractEditStepAction: {
            return {
                ...state,
                currentStep: action.step,
            };
        }
        case actions.goToPreviousContractEditStepAction: {
            return {
                ...state,
                currentStep: state.currentStep > 0 ? state.currentStep - 1 : state.currentStep,
            };
        }
        case actions.changeCurrencyPaymentScheduleAction: {
            return {
                ...state,
                previousPaymentSchedule: action.paymentSchedule,
                nextCurrency: action.currency,
                shouldRecreatePaymentSchedule: true,
            };
        }
        case actions.postPaymentScheduleSuccessAction: {
            return {
                ...state,
                entity: state.entity
                    ? {
                          ...state.entity,
                          paymentSchedule: action.response,
                      }
                    : null,
            };
        }
        case actions.putPaymentScheduleSuccessAction: {
            return {
                ...state,
                entity: state.entity
                    ? {
                          ...state.entity,
                          paymentSchedule: action.response,
                      }
                    : null,
            };
        }
        case actions.postInstalmentSuccessAction: {
            return {
                ...state,
                entity: state.entity
                    ? {
                          ...state.entity,
                          paymentSchedule: action.response,
                      }
                    : null,
            };
        }
        case actions.putInstalmentSuccessAction: {
            return {
                ...state,
                entity: state.entity
                    ? {
                          ...state.entity,
                          paymentSchedule: action.response,
                      }
                    : null,
            };
        }
        case actions.deleteInstalmentSuccessAction: {
            return {
                ...state,
                entity: state.entity
                    ? {
                          ...state.entity,
                          paymentSchedule: action.response,
                      }
                    : null,
            };
        }
        default:
            return state;
    }
};

export const contractStatusReducer = (
    state: ContractStatusState = {},
    action: AllActions,
): ContractStatusState => {
    switch (action.type) {
        case actions.fetchContractSuccessAction: {
            return {
                ...state,
                get: SUCCESS,
            };
        }
        case actions.fetchContractFailureAction: {
            return {
                ...state,
                get: ERROR,
            };
        }
        case actions.fetchContractRequestAction: {
            return {
                ...state,
                get: PENDING,
            };
        }
        case actions.postContractRequestAction: {
            return {
                ...state,
                post: PENDING,
            };
        }
        case actions.postContractSuccessAction: {
            return {
                ...state,
                post: SUCCESS,
            };
        }
        case actions.postContractFailureAction: {
            return {
                ...state,
                post: ERROR,
            };
        }
        default:
            return state;
    }
};

export const legalInformationReducer = (
    state: LegalInformationState = {
        entity: [],
        error: '',
    },
    action: LegalInformationActions,
): LegalInformationState => {
    switch (action.type) {
        case actions.fetchLegalInformationSuccessAction: {
            return {
                ...state,
                entity: action.response,
            };
        }
        case actions.fetchLegalInformationFailureAction: {
            return {
                ...state,
                error: action.error,
            };
        }
        default:
            return state;
    }
};

export const legalInformationStatusReducer = (
    state: LegalInformationStatusState = {},
    action: AllActions,
): LegalInformationStatusState => {
    switch (action.type) {
        case actions.fetchLegalInformationSuccessAction: {
            return {
                ...state,
                get: SUCCESS,
            };
        }
        case actions.fetchLegalInformationFailureAction: {
            return {
                ...state,
                get: ERROR,
            };
        }
        case actions.fetchLegalInformationRequestAction: {
            return {
                ...state,
                get: PENDING,
            };
        }
        default:
            return state;
    }
};

export const quotePricingReducer = (
    state: QuotePricingState = {
        entity: null,
        uploadedNotAttachedProofOfDocuments: [],
        failedProofOfDocuments: [],
        error: '',
    },
    action: QuotePricingActions,
): QuotePricingState => {
    switch (action.type) {
        case actions.fetchQuotePricingSuccessAction: {
            return {
                ...state,
                entity: action.response,
            };
        }
        case actions.fetchQuotePricingFailureAction: {
            return {
                ...state,
                error: action.error,
            };
        }
        case actions.postProofOfDocumentsSuccessAction: {
            return {
                ...state,
                uploadedNotAttachedProofOfDocuments: [
                    ...state.uploadedNotAttachedProofOfDocuments,
                    action.response,
                ],
                failedProofOfDocuments: filterByFileName(
                    state.failedProofOfDocuments,
                    action.fileName,
                ),
            };
        }
        case actions.postProofOfDocumentsFailureAction: {
            return {
                ...state,
                failedProofOfDocuments: [
                    ...new Set([...state.failedProofOfDocuments, action.file]),
                ],
            };
        }
        case actions.deleteProofOfDocumentsSuccessAction: {
            return {
                ...state,
                entity: state.entity
                    ? {
                          ...state.entity,
                          proofDocuments: filterByFileName(
                              state.entity?.proofDocuments,
                              action.fileName,
                          ),
                      }
                    : null,
                uploadedNotAttachedProofOfDocuments: filterByFileName(
                    state.uploadedNotAttachedProofOfDocuments,
                    action.fileName,
                ),
                failedProofOfDocuments: filterByFileName(
                    state.failedProofOfDocuments,
                    action.fileName,
                ),
            };
        }
        case actions.postChangeQuotePricesSuccessAction: {
            return {
                ...state,
                entity: action.response,
                uploadedNotAttachedProofOfDocuments: [],
            };
        }
        default:
            return state;
    }
};

export const quotePricingStatusReducer = (
    state: QuotePricingStatusState = {},
    action: AllActions,
): QuotePricingStatusState => {
    switch (action.type) {
        case actions.fetchQuotePricingSuccessAction: {
            return {
                ...state,
                get: SUCCESS,
            };
        }
        case actions.fetchQuotePricingFailureAction: {
            return {
                ...state,
                get: ERROR,
            };
        }
        case actions.fetchQuotePricingRequestAction: {
            return {
                ...state,
                get: PENDING,
            };
        }
        case actions.postChangeQuotePricesRequestAction: {
            return {
                ...state,
                post: PENDING,
            };
        }
        case actions.postChangeQuotePricesFailureAction: {
            return {
                ...state,
                post: ERROR,
            };
        }
        case actions.postChangeQuotePricesSuccessAction: {
            return {
                ...state,
                post: SUCCESS,
            };
        }
        case actions.postProofOfDocumentsSuccessAction: {
            return {
                ...state,
                postProofDocuments: {
                    ...state.postProofDocuments,
                    [action.fileName]: SUCCESS,
                },
            };
        }
        case actions.postProofOfDocumentsFailureAction: {
            return {
                ...state,
                postProofDocuments: {
                    ...state.postProofDocuments,
                    [action.file.name]: ERROR,
                },
            };
        }
        case actions.postProofOfDocumentsRequestAction: {
            return {
                ...state,
                postProofDocuments: {
                    ...state.postProofDocuments,
                    [action.file.name]: PENDING,
                },
            };
        }
        default:
            return state;
    }
};

export const paymentScheduleStatusReducer = (
    state: PaymentScheduleStatusState = {},
    action: AllActions,
): PaymentScheduleStatusState => {
    switch (action.type) {
        case actions.postPaymentScheduleRequestAction: {
            return {
                ...state,
                post: PENDING,
            };
        }
        case actions.postPaymentScheduleSuccessAction: {
            return {
                ...state,
                post: SUCCESS,
            };
        }
        case actions.postPaymentScheduleFailureAction: {
            return {
                ...state,
                post: ERROR,
            };
        }
        case actions.putPaymentScheduleSuccessAction: {
            return {
                ...state,
                put: SUCCESS,
            };
        }
        case actions.putPaymentScheduleFailureAction: {
            return {
                ...state,
                put: ERROR,
            };
        }
        case actions.putPaymentScheduleRequestAction: {
            return {
                ...state,
                put: PENDING,
            };
        }
        default:
            return state;
    }
};
